import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import FormItem from '../../../common/form/FormItem';
import FormModal from '../../../common/form/FormModal';
import FormPanel from '../../../common/form/FormPanel';
import FormWidget from '../../../common/form/FormWidget';
import { Link, Typography } from '@mui/material';
import I18n, { useI18n } from '../../../common/i18n/I18n';
import { FormContainer } from '../../../common/form/FormContainer';
import ResumeRenderer from './ResumeRenderer';
import { Icons } from '../../../common/icons/Icons';
import { H } from '../../../common/Layout';
import { useSaveResume } from './annotationApi';
import { Reference } from '../../../common/reference/reference';
import { FormProvider } from '../../../common/form/FormContext';
import { documentExtensions } from '../../../common/attachments/attachmentsApi';
import ShowrunnrAIInfoModal from './ShowrunnrAIInfoModal';
import { BasicInfoInputExtended } from '../detailsPageUtilities/personBasicInformation';
import { SHOWRUNNR_POLICY_LINK } from '../../../common/auth/PolicyLink';
import useAISettings from '../../../common/hooks/useAISettings';

export default ({
    isOpen,
    onClose,
    selectedFile,
    entityReference,
    setSelectedFile,
    isUploading,
    setState
}: {
    isOpen: boolean;
    onClose: () => void;
    selectedFile: File | null;
    entityReference: Reference;
    setSelectedFile: Dispatch<SetStateAction<File | null>>;
    isUploading: boolean;
    setState: Dispatch<SetStateAction<BasicInfoInputExtended>>;
}) => {
    const { onSaveResume, saveResumeLoading } = useSaveResume();
    const [values, setValues] = useState<{ piiConsent: boolean; selectedFile?: File }>({ piiConsent: true });
    const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
    const [savingResume, setSavingResume] = useState(false);
    const isAIEnabled = useAISettings();

    const onSaveResumeHandle = async () => {
        setSavingResume(true);
        onSaveResume(entityReference, !isAIEnabled ? false : values.piiConsent).then(() => {
            setState((prevData) => ({ ...prevData, resumeReference: entityReference }));
            values?.piiConsent && isAIEnabled ? setIsInfoModalOpen(true) : setSelectedFile(null);
            setSavingResume(false);
        });
    };

    useEffect(() => {
        if (selectedFile) {
            setValues({ ...values, selectedFile });
        }
    }, [selectedFile]);

    const onCloseHandle = () => {
        setIsInfoModalOpen(false);
        setSelectedFile(null);
    };

    return (
        <>
            <FormProvider state={values} setState={setValues}>
                <FormModal
                    isOpen={isOpen}
                    onClose={onClose}
                    id={'upload-resume'}
                    title={<I18n token={'upload.resume.title'} />}
                    onSave={onSaveResumeHandle}
                    isSaving={saveResumeLoading || savingResume}
                    isLoading={isUploading}
                    isSaveDisabled={isUploading}
                >
                    <FormPanel>
                        <FormContainer hidden={!isAIEnabled}>
                            <FormItem>
                                <H gap={1}>
                                    <Icons.AutoAwesomeGradient fontSize="large" />
                                    <Typography fontSize={'24px'} fontWeight={'600'}>
                                        <I18n token={'upload.AI.title'} />
                                    </Typography>
                                </H>
                            </FormItem>
                        </FormContainer>
                        <FormContainer hidden={!isAIEnabled}>
                            <FormItem>
                                <Typography>
                                    <I18n token={'upload.resume.description'} />
                                </Typography>
                            </FormItem>
                        </FormContainer>
                        <FormContainer containerSx={{ pt: 2 }}>
                            <FormItem>
                                {values?.selectedFile ? (
                                    <ResumeRenderer
                                        selectedFile={selectedFile}
                                        onSuccess={() => setValues({ ...values, selectedFile: null })}
                                        setState={setState}
                                    />
                                ) : (
                                    <FormWidget
                                        name="selectedFile"
                                        component="Dropzone"
                                        filesLimit={1}
                                        acceptedFiles={['application/pdf', ...documentExtensions]}
                                        dropzoneText={useI18n('upload.resume.dropzone')}
                                        showPreviews={false}
                                    />
                                )}
                            </FormItem>
                        </FormContainer>
                        <FormContainer containerSx={{ pt: 2 }} hidden={!isAIEnabled}>
                            <FormItem>
                                <FormWidget
                                    component="Checkbox"
                                    name="piiConsent"
                                    sx={{ '.MuiFormControlLabel-root': { mr: 0 } }}
                                />
                                <Typography>
                                    <I18n token={'common.AI.consent'} />
                                    <Link href={SHOWRUNNR_POLICY_LINK} sx={{ textDecoration: 'none' }} target="_blank">
                                        <I18n token={'common.AI.privacy'} />
                                    </Link>
                                </Typography>
                            </FormItem>
                        </FormContainer>
                    </FormPanel>
                </FormModal>
            </FormProvider>
            {isInfoModalOpen && <ShowrunnrAIInfoModal isOpen={isInfoModalOpen} onClose={onCloseHandle} />}
        </>
    );
};
